import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { api } from 'src/api';
import { NO_OPTIONS } from 'src/stores/utilitySetups/companySetupsRead';
import { linkLine, setupEntries, utilityEntries } from './historyLines';
import useStyles from './styles';
const SHOWN = 5;

/** A history's newest items, with Show all. Shared with the setups list (LB-6193). */
export const HistoryList = _ref => {
  let title = _ref.title,
    lead = _ref.lead,
    items = _ref.items,
    empty = _ref.empty;
  const classes = useStyles();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    all = _useState2[0],
    setAll = _useState2[1];
  const shown = all ? items : items.slice(0, SHOWN);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.historyPart
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "h6",
    className: classes.historyTitle
  }, title), lead && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, lead), items.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, empty) : /*#__PURE__*/React.createElement("ul", {
    className: classes.historyList
  }, shown.map(item => /*#__PURE__*/React.createElement("li", {
    key: item.key
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span",
    className: classes.historyHeading
  }, item.heading), item.lines.map((line, index) => /*#__PURE__*/React.createElement(Typography, {
    key: "".concat(index, "|").concat(line),
    variant: "body2",
    color: "light",
    component: "span",
    display: "block"
  }, line))))), items.length > SHOWN && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    className: classes.historyMore,
    onClick: () => setAll(value => !value)
  }, all ? 'Show fewer' : "Show all ".concat(items.length)));
};
export const Failed = _ref2 => {
  let text = _ref2.text,
    onRetry = _ref2.onRetry;
  return /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: onRetry
    }, "Retry")
  }, text);
};
export const Loading = () => /*#__PURE__*/React.createElement(Typography, {
  variant: "body2",
  color: "light",
  component: "p"
}, "Loading the history\u2026");

/**
 * The history under the Setup section (LB-6192, PRD V3 FR-15): this utility's links and billing options, each change
 * naming the setup it came through, and the setup's edits with how many utilities each changed.
 *
 * Read only when opened: the page shows a section per utility. `setupHistory` is the section's own read of the setup's
 * history (undefined while it's read, null when it can't be), which its Last change row uses too. `version` changes after
 * any setup change on the page, so an open history reads again; so does a company switch, and the last company's answer
 * never shows.
 */
export const SetupHistoryPanel = _ref3 => {
  let companyId = _ref3.companyId,
    propertyUtilityId = _ref3.propertyUtilityId,
    setupId = _ref3.setupId,
    setupHistory = _ref3.setupHistory,
    onRetrySetupHistory = _ref3.onRetrySetupHistory,
    version = _ref3.version;
  const classes = useStyles();
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    open = _useState4[0],
    setOpen = _useState4[1];
  // status: 'loading' | 'ready' | 'failed' | 'hidden' (the caller may not read it).
  const _useState5 = useState({
      key: null,
      status: 'loading',
      history: null
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    read = _useState6[0],
    setRead = _useState6[1];
  const _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    attempt = _useState8[0],
    setAttempt = _useState8[1];
  const key = "".concat(companyId, "|").concat(propertyUtilityId);
  useEffect(() => {
    if (!open || !propertyUtilityId) return undefined;
    let current = true;
    // A read again keeps the last answer on screen until the new one arrives; another utility's or company's never shows.
    setRead(last => last.key === key && last.status === 'ready' ? last : {
      key,
      status: 'loading',
      history: null
    });
    (async () => {
      try {
        const resp = await api.UtilitySetups.getBillingOptionHistory({
          propertyUtilityId
        }, NO_OPTIONS);
        if (!current) return;
        const history = resp === null || resp === void 0 ? void 0 : resp.response;
        if (!(history !== null && history !== void 0 && history.setup)) throw new Error('No history answer');
        setRead({
          key,
          status: 'ready',
          history
        });
      } catch (err) {
        var _err$response;
        if (!current) return;
        const hidden = [403, 404].includes(err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 ? void 0 : _err$response.status);
        setRead({
          key,
          status: hidden ? 'hidden' : 'failed',
          history: null
        });
      }
    })();
    return () => {
      current = false;
    };
  }, [open, key, propertyUtilityId, version, attempt]);
  if (!propertyUtilityId) return null;
  const utility = read.key === key ? read : {
    status: 'loading'
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.history
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    className: classes.historyToggle,
    "aria-expanded": open,
    onClick: () => setOpen(value => !value)
  }, open ? 'Hide history' : 'Show history'), open && /*#__PURE__*/React.createElement(React.Fragment, null, utility.status === 'loading' && /*#__PURE__*/React.createElement(Loading, null), utility.status === 'failed' && /*#__PURE__*/React.createElement(Failed, {
    text: "Couldn't load this utility's history.",
    onRetry: () => setAttempt(value => value + 1)
  }), utility.status === 'ready' && /*#__PURE__*/React.createElement(HistoryList, {
    title: "This utility",
    lead: linkLine(utility.history),
    items: utilityEntries(utility.history),
    empty: "No changes recorded yet."
  }), utility.status === 'hidden' && !setupId && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "No history to show for this utility."), setupId && setupHistory === undefined && /*#__PURE__*/React.createElement(Loading, null), setupId && setupHistory === null && /*#__PURE__*/React.createElement(Failed, {
    text: "Couldn't load this setup's history.",
    onRetry: onRetrySetupHistory
  }), setupId && setupHistory && /*#__PURE__*/React.createElement(HistoryList, {
    title: "This setup",
    items: setupEntries(setupHistory),
    empty: "No changes recorded yet."
  })));
};